import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startRegistration, startAuthentication } from "@simplewebauthn/browser";
import { Fingerprint, KeyRound, ShieldCheck, Loader2, Lock, Eye, EyeOff } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { InputOTP, InputOTPGroup } from "@/components/ui/input-otp";
import { OTPInputContext } from "input-otp";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

const MaskedSlot = ({ index, mask }) => {
  const ctx = React.useContext(OTPInputContext);
  const slot = (ctx && ctx.slots[index]) || {};
  const { char, hasFakeCaret, isActive } = slot;
  return (
    <div className={cn(
      "relative flex items-center justify-center w-11 h-12 text-lg font-mono-ax rounded-md border bg-[#050505] border-[#1F1F24] text-[#FFD700] transition-all",
      isActive && "z-10 ring-1 ring-[#D4AF37] border-[#D4AF37]"
    )}>
      {char ? (mask ? "•" : char) : ""}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-5 w-px bg-[#D4AF37] animate-pulse" />
        </div>
      )}
    </div>
  );
};

const AccessComando = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [modo, setModo] = useState("cargando"); // cargando | setup | login
  const [pin, setPin] = useState("");
  const [showPin, setShowPin] = useState(false);
  const [expiredNotice] = useState(() => {
    try {
      const v = sessionStorage.getItem("axiora_expired") === "1";
      if (v) sessionStorage.removeItem("axiora_expired");
      return v;
    } catch (e) { return false; }
  });
  const [setupCode, setSetupCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");

  useEffect(() => {
    document.title = "AXIORA";
    (async () => {
      try {
        const { data } = await api.get("/auth/status");
        setModo(data.inicializado ? "login" : "setup");
      } catch {
        setModo("login");
      }
    })();
  }, []);

  const parseErr = (e) => {
    if (e?.response?.status === 429) return "Demasiados intentos. IP bloqueada temporalmente.";
    if (e?.response?.data?.detail) return e.response.data.detail;
    if (e?.name === "NotAllowedError") return "Verificación biométrica cancelada o no permitida.";
    return "Ocurrió un error. Intenta de nuevo.";
  };

  const handleSetup = async () => {
    setError(""); setOkMsg("");
    if (pin.length !== 6) { setError("El PIN debe tener 6 dígitos."); return; }
    if (!setupCode.trim()) { setError("Ingresa el código de instalación."); return; }
    setBusy(true);
    try {
      const { data: opts } = await api.post("/setup/options", { setup_code: setupCode.trim() });
      const credential = await startRegistration({ optionsJSON: opts });
      await api.post("/setup/finish", { setup_code: setupCode.trim(), pin, credential });
      setOkMsg("Passkey y PIN registrados. Ya puedes acceder.");
      setModo("login"); setPin(""); setSetupCode("");
    } catch (e) {
      setError(parseErr(e));
    } finally { setBusy(false); }
  };

  const handleLogin = async () => {
    setError(""); setOkMsg("");
    if (pin.length !== 6) { setError("El PIN debe tener 6 dígitos."); return; }
    setBusy(true);
    try {
      const { data: opts } = await api.post("/auth/options");
      const credential = await startAuthentication({ optionsJSON: opts });
      const { data } = await api.post("/auth/verify", { credential, pin });
      login(data.token);
      navigate("/comando/matriz", { replace: true });
    } catch (e) {
      setError(parseErr(e));
      setPin("");
    } finally { setBusy(false); }
  };

  if (modo === "cargando") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  const esSetup = modo === "setup";

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-[#050505] relative overflow-hidden px-6">
      <div className="absolute inset-0 opacity-[0.06] pointer-events-none"
        style={{ background: "radial-gradient(circle at 50% 30%, #D4AF37 0%, transparent 55%)" }} />

      <div data-testid="acceso-panel" className="relative z-10 w-full max-w-md ax-fade-up">
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-full border border-[#D4AF37]/40 flex items-center justify-center mb-4 bg-[#0A0A0A]">
            <Lock className="w-6 h-6 text-[#D4AF37]" />
          </div>
          <h1 className="font-serif-ax text-4xl tracking-[0.15em] ax-gold-text">AXIORA</h1>
          <p className="mt-2 text-[#71717A] text-xs tracking-[0.3em] uppercase">
            {esSetup ? "Inicialización del Centro" : "Acceso Restringido"}
          </p>
        </div>

        <div className="bg-[#0A0A0A] border border-[#1F1F24] rounded-xl p-7 shadow-2xl">
          {expiredNotice && !esSetup && (
            <div data-testid="sesion-expirada" className="mb-5 text-sm text-[#F39C12] text-center bg-[#F39C12]/10 border border-[#F39C12]/25 rounded-md py-2 px-3">
              Tu sesión expiró por seguridad. Vuelve a acceder con tu passkey y PIN.
            </div>
          )}
          {esSetup && (
            <div className="mb-5">
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">Código de instalación</label>
              <Input
                data-testid="setup-code-input"
                type="password"
                value={setupCode}
                onChange={(e) => setSetupCode(e.target.value)}
                placeholder="Código de un solo uso"
                className="bg-[#050505] border-[#1F1F24] text-[#F5F5F7] font-mono-ax focus-visible:ring-[#D4AF37]"
              />
            </div>
          )}

          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider flex items-center gap-2">
                <KeyRound className="w-3.5 h-3.5" /> PIN maestro (6 dígitos)
              </label>
              <button
                type="button"
                data-testid="toggle-pin-visibility"
                onClick={() => setShowPin((v) => !v)}
                aria-label={showPin ? "Ocultar PIN" : "Mostrar PIN"}
                className="flex items-center gap-1 text-[11px] text-[#71717A] hover:text-[#D4AF37] transition-colors"
              >
                {showPin ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                {showPin ? "Ocultar" : "Mostrar"}
              </button>
            </div>
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={pin} onChange={setPin} data-testid="pin-input">
                <InputOTPGroup className="gap-2">
                  {[0,1,2,3,4,5].map((i) => (
                    <MaskedSlot key={i} index={i} mask={!showPin} />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
          </div>

          {error && (
            <div data-testid="acceso-error" className="mb-4 text-sm text-[#FF6B5B] text-center bg-[#FF6B5B]/10 border border-[#FF6B5B]/25 rounded-md py-2 px-3">
              {error}
            </div>
          )}
          {okMsg && (
            <div data-testid="acceso-ok" className="mb-4 text-sm text-[#4ECDC4] text-center bg-[#4ECDC4]/10 border border-[#4ECDC4]/25 rounded-md py-2 px-3">
              {okMsg}
            </div>
          )}

          <button
            data-testid={esSetup ? "setup-submit-btn" : "login-submit-btn"}
            onClick={esSetup ? handleSetup : handleLogin}
            disabled={busy}
            className="w-full h-12 rounded-lg bg-gradient-to-r from-[#D4AF37] to-[#FFD700] text-[#0A0A0A] font-semibold tracking-wide flex items-center justify-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : (esSetup ? <ShieldCheck className="w-5 h-5" /> : <Fingerprint className="w-5 h-5" />)}
            {esSetup ? "Registrar Passkey + PIN" : "Acceder con Passkey + PIN"}
          </button>

          <p className="mt-4 text-center text-[11px] text-[#52525b] leading-relaxed">
            {esSetup
              ? "Registra tu passkey (Face ID / Touch ID) y define tu PIN. El código de instalación se invalida tras el primer uso."
              : "Se requiere tu passkey biométrica y tu PIN maestro. Ambos son obligatorios."}
          </p>

          <div className="mt-5 pt-4 border-t border-[#1F1F24] text-center">
            {esSetup ? (
              <button
                type="button"
                data-testid="volver-login-btn"
                onClick={() => { setModo("login"); setError(""); setOkMsg(""); setSetupCode(""); setPin(""); }}
                className="text-[11px] text-[#71717A] hover:text-[#D4AF37] transition-colors"
              >
                ← Volver a acceder con mi passkey
              </button>
            ) : (
              <button
                type="button"
                data-testid="reinicializar-btn"
                onClick={() => { setModo("setup"); setError(""); setOkMsg(""); setPin(""); }}
                className="text-[11px] text-[#71717A] hover:text-[#D4AF37] transition-colors"
              >
                ¿Cambiaste de dominio o dispositivo? Reinicializar con código
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessComando;
